/**
 * The reel stage paints a snapshot. A Storage download URL often will not
 * draw into that snapshot, so a saved photo is read back as bytes first.
 */

import type { FabricCanvasData } from '@canvas/shared-types';
import { getBlob, ref } from 'firebase/storage';
import { getClientStorage } from '@canvas/firebase-config/client';
import { withOffscreenCanvas } from '@/lib/offscreenCanvas';
import { REEL_CAPTION_KEY } from './reelCaption';
import { REEL_IMAGE_KEY } from './reelImage';

/** Storage object path encoded in a Firebase download URL, or ''. */
function storagePathFromUrl(src: string): string {
  try {
    const url = new URL(src);
    const mark = '/o/';
    const at = url.pathname.indexOf(mark);
    if (!url.hostname.endsWith('firebasestorage.googleapis.com') || at < 0) return '';
    return decodeURIComponent(url.pathname.slice(at + mark.length));
  } catch {
    return '';
  }
}

export interface LocalReelImages {
  data: FabricCanvasData;
  /** True when a remote photo could not be read. */
  missed: boolean;
  revoke: () => void;
}

/** Copy page JSON, swapping remote reel photos for blob URLs the snapshot can draw. */
export async function localReelImages(data: FabricCanvasData): Promise<LocalReelImages> {
  const urls: string[] = [];
  let missed = false;
  const objects = await Promise.all(
    (data.objects || []).map(async (obj) => {
      if (obj[REEL_IMAGE_KEY] !== true || typeof obj.src !== 'string') return obj;
      const src = obj.src;
      if (src.startsWith('data:') || src.startsWith('blob:')) {
        const copy = { ...obj };
        delete copy.crossOrigin;
        return copy;
      }
      const stored = typeof obj.craftaraReelImagePath === 'string' ? obj.craftaraReelImagePath : '';
      const path = stored || storagePathFromUrl(src);
      let blob: Blob | null = null;
      if (path) {
        try {
          blob = await getBlob(ref(getClientStorage(), path));
        } catch {
          blob = null;
        }
      }
      if (!blob) {
        try {
          const res = await fetch(src);
          if (res.ok) blob = await res.blob();
        } catch {
          blob = null;
        }
      }
      if (!blob) {
        missed = true;
        return obj;
      }
      const url = URL.createObjectURL(blob);
      urls.push(url);
      const copy = { ...obj, src: url };
      delete copy.crossOrigin;
      return copy;
    })
  );
  return {
    data: { ...data, objects },
    missed,
    revoke: () => urls.forEach((url) => URL.revokeObjectURL(url)),
  };
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not draw that photo.'));
    img.src = src;
  });
}

/**
 * Paint the page. Reel photos are drawn from local bytes. Everything else
 * (caption, clip poster) is the normal page render on top.
 */
export async function renderReelFrame(data: FabricCanvasData): Promise<string> {
  const width = data.width || 1080;
  const height = data.height || 1920;
  const scale = 0.5;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not draw the frame.');
  ctx.fillStyle = typeof data.background === 'string' && data.background ? data.background : '#111111';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  for (const obj of data.objects || []) {
    if (obj[REEL_IMAGE_KEY] !== true || typeof obj.src !== 'string') continue;
    if (!obj.src.startsWith('data:') && !obj.src.startsWith('blob:')) continue;
    const img = await loadHtmlImage(obj.src);
    const cover = Math.max(canvas.width / img.width, canvas.height / img.height);
    const w = img.width * cover;
    const h = img.height * cover;
    ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
  }

  const rest = (data.objects || []).filter(
    (obj) => obj[REEL_IMAGE_KEY] !== true && obj[REEL_CAPTION_KEY] !== true
  );
  if (rest.length) {
    try {
      const overlay = await withOffscreenCanvas(
        { ...data, objects: rest, background: '' },
        true,
        (fabricCanvas) =>
          fabricCanvas.toDataURL({ format: 'png', multiplier: scale, enableRetinaScaling: false })
      );
      const overlayImg = await loadHtmlImage(overlay);
      ctx.drawImage(overlayImg, 0, 0, canvas.width, canvas.height);
    } catch {
      // The photo stays. A clip poster that cannot draw is left off this snapshot.
    }
  }
  return canvas.toDataURL('image/jpeg', 0.82);
}
